import { parseCSV } from './csv';
import { normalizeDraftManager } from './keepers';
import { ACTIVE_MEMBERS } from './managers';

export const GAUNTLET_TARGET_STREAK = 3;
export const GAUNTLET_TIME_LIMIT_SECONDS = 20;
export const GAUNTLET_DRAFT_YEARS = [
  2013, 2014, 2015, 2016, 2017, 2018, 2019, 2020, 2021, 2022, 2023, 2024, 2025,
];

const HTML_FALLBACK = /^\s*(<!doctype html|<html)/i;
const MEMORABLE_DRAFT_POSITIONS = new Set(['QB', 'RB', 'WR', 'TE']);

function toNumber(value) {
  const n = parseFloat(value);
  return Number.isFinite(n) ? n : null;
}

function toInteger(value) {
  const n = parseInt(value, 10);
  return Number.isInteger(n) ? n : null;
}

function normalizeManager(name) {
  return normalizeDraftManager(String(name || '').trim());
}

function stableHash(value) {
  const text = String(value || '');
  let hash = 2166136261;
  for (let i = 0; i < text.length; i += 1) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

function seededRandom(seed) {
  let state = stableHash(seed) || 1;
  return () => {
    state += 0x6D2B79F5;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function seededShuffle(items, seed = 'keeper-gauntlet') {
  const random = seededRandom(seed);
  const out = items.slice();
  for (let i = out.length - 1; i > 0; i -= 1) {
    const j = Math.floor(random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

function unique(values) {
  return Array.from(new Set(values.filter((value) => value !== undefined && value !== null && value !== '')));
}

function makeChoices(correct, candidates, seed, minChoices = 4) {
  const distractors = seededShuffle(
    unique(candidates).filter((candidate) => candidate !== correct),
    `${seed}:distractors`
  );
  const choices = unique([correct, ...distractors]).slice(0, minChoices);
  if (choices.length < minChoices || !choices.includes(correct)) return [];
  return seededShuffle(choices, `${seed}:choices`);
}

function addQuestion(questions, question) {
  if (!question.id || !question.prompt || !question.answer) return;
  if (!Array.isArray(question.choices) || question.choices.length < 4) return;
  if (!question.choices.includes(question.answer)) return;

  questions.push({
    difficulty: 'hard',
    category: 'League Receipts',
    sourceLabel: 'Bruins09 archive',
    ...question,
  });
}

function managersFromRows(rows) {
  return unique([
    ...ACTIVE_MEMBERS,
    ...rows.map((row) => normalizeManager(row.Manager)),
    ...rows.map((row) => normalizeManager(row['Team1 Manager'])),
    ...rows.map((row) => normalizeManager(row['Team2 Manager'])),
    ...rows.map((row) => normalizeManager(row.Champion)),
    ...rows.map((row) => normalizeManager(row.manager)),
    ...rows.map((row) => normalizeManager(row.opponent)),
  ]);
}

function groupBy(rows, keyFn) {
  return rows.reduce((map, row) => {
    const key = keyFn(row);
    if (!key) return map;
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(row);
    return map;
  }, new Map());
}

function isMemorableDraftPick(row) {
  const round = toInteger(row.Round);
  return round === 1 && MEMORABLE_DRAFT_POSITIONS.has(row.Position);
}

function buildDraftQuestions(questions, draftRows, allManagers) {
  const byYear = groupBy(draftRows, (row) => row.Season);

  byYear.forEach((rows, year) => {
    const yearManagers = managersFromRows(rows);

    rows.filter(isMemorableDraftPick).forEach((row) => {
      const player = row.Player_Name;
      const manager = normalizeManager(row.Manager);
      const round = toInteger(row.Round);
      const overall = toInteger(row.Pick_Overall);
      if (!player || !manager || !round || !overall) return;

      addQuestion(questions, {
        id: `draft-manager-${year}-${overall}`,
        category: 'Draft Receipts',
        difficulty: 'hard',
        sourceLabel: `${year} draft`,
        prompt: `Who drafted ${player} in ${year}?`,
        answer: manager,
        choices: makeChoices(manager, yearManagers.length >= 4 ? yearManagers : allManagers, `draft-manager-${year}-${overall}`),
        explanation: `${manager} drafted ${player} in Round ${round}, Pick ${overall}.`,
      });
    });
  });
}

function buildStandingsQuestions(questions, standingsRows, allManagers) {
  const byYear = groupBy(standingsRows, (row) => row.Year);
  const years = unique(standingsRows.map((row) => toInteger(row.Year)))
    .filter(Boolean)
    .sort((a, b) => a - b);
  const memorableTeamYears = new Set([
    years[0],
    years[years.length - 1],
  ]);

  byYear.forEach((rows, year) => {
    const managerChoices = managersFromRows(rows);
    const teamNames = unique(rows.map((row) => row.Team));
    const pointsRows = rows
      .map((row) => ({
        row,
        pointsFor: toNumber(row['Points For']),
        wins: toNumber(row.Wins),
        losses: toNumber(row.Losses),
      }))
      .filter((entry) => entry.pointsFor !== null && entry.wins !== null && entry.losses !== null);

    const pointsLeader = pointsRows.slice().sort((a, b) => b.pointsFor - a.pointsFor)[0];
    const worstRecord = pointsRows.slice().sort((a, b) => a.wins - b.wins || a.pointsFor - b.pointsFor)[0];

    [
      ['points-leader', 'Who led the league in Points For in', pointsLeader, 'highest points-for'],
      ['worst-record', 'Who had the worst regular-season record in', worstRecord, 'worst record'],
    ].forEach(([key, promptPrefix, entry, label]) => {
      if (!entry) return;
      const manager = normalizeManager(entry.row.Manager);
      addQuestion(questions, {
        id: `standings-${key}-${year}`,
        category: 'Season Stats',
        difficulty: key.includes('points') ? 'hard' : 'medium',
        sourceLabel: `${year} standings`,
        prompt: `${promptPrefix} ${year}?`,
        answer: manager,
        choices: makeChoices(manager, managerChoices.length >= 4 ? managerChoices : allManagers, `standings-${key}-${year}`),
        explanation: `${manager} had the ${label} in ${year}.`,
      });
    });

    rows.forEach((row) => {
      const manager = normalizeManager(row.Manager);
      const team = row.Team;
      if (!manager || !team) return;
      if (!memorableTeamYears.has(toInteger(year))) return;

      addQuestion(questions, {
        id: `standings-team-${year}-${manager}`,
        category: 'Team Names',
        difficulty: 'hard',
        sourceLabel: `${year} standings`,
        prompt: `What was ${manager}'s team name in ${year}?`,
        answer: team,
        choices: makeChoices(team, teamNames, `standings-team-${year}-${manager}`),
        explanation: `${manager}'s ${year} team name was ${team}.`,
      });
    });
  });
}

function isPlayoffChampion(row) {
  return /Playoff Champion/i.test(row.Method || '');
}

function buildChampionshipQuestions(questions, championshipRows, allManagers) {
  const playoffChampions = championshipRows.filter(isPlayoffChampion);
  const championManagers = unique(playoffChampions.map((row) => normalizeManager(row.Champion)));
  const championTeams = unique(playoffChampions.map((row) => row.Team));
  const years = unique(playoffChampions.map((row) => row.Year));

  playoffChampions.forEach((row) => {
    const year = row.Year;
    const champion = normalizeManager(row.Champion);
    const team = row.Team;

    addQuestion(questions, {
      id: `champion-manager-${year}`,
      category: 'Championships',
      difficulty: 'medium',
      sourceLabel: `${year} championship`,
      prompt: `Who won the ${year} Bruins09 championship?`,
      answer: champion,
      choices: makeChoices(champion, unique([...championManagers, ...allManagers]), `champion-manager-${year}`),
      explanation: `${champion} won the ${year} championship with ${team}.`,
    });

    addQuestion(questions, {
      id: `champion-team-${year}`,
      category: 'Championships',
      difficulty: 'hard',
      sourceLabel: `${year} championship`,
      prompt: `What team name won the ${year} Bruins09 championship?`,
      answer: team,
      choices: makeChoices(team, championTeams, `champion-team-${year}`),
      explanation: `${team} was the ${year} championship team name.`,
    });
  });

  championManagers.forEach((manager) => {
    const managerYears = playoffChampions
      .filter((row) => normalizeManager(row.Champion) === manager)
      .map((row) => row.Year);
    if (managerYears.length === 0) return;
    const correctYear = managerYears[0];
    addQuestion(questions, {
      id: `champion-year-${manager}`,
      category: 'Championships',
      difficulty: managerYears.length > 1 ? 'medium' : 'hard',
      sourceLabel: 'Championship ledger',
      prompt: `Which of these years was a championship year for ${manager}?`,
      answer: String(correctYear),
      choices: makeChoices(String(correctYear), years.map(String), `champion-year-${manager}`),
      explanation: `${manager}'s title year list includes ${managerYears.join(', ')}.`,
    });
  });
}

function buildRivalryQuestions(questions, rivalryRows, allManagers) {
  const seenPairs = new Set();

  rivalryRows.forEach((row) => {
    const manager = normalizeManager(row.manager);
    const opponent = normalizeManager(row.opponent);
    const wins = toInteger(row.wins);
    const losses = toInteger(row.losses);
    const totalGames = toInteger(row.total_games);
    if (!manager || !opponent || manager === opponent || !totalGames || totalGames < 16 || wins === null || losses === null) return;

    const pairKey = [manager, opponent].sort().join('::');
    if (seenPairs.has(pairKey)) return;
    seenPairs.add(pairKey);

    const leader = wins > losses ? manager : losses > wins ? opponent : 'It is tied';
    const record = `${wins}-${losses}`;

    addQuestion(questions, {
      id: `rivalry-leader-${pairKey}`,
      category: 'Rivalries',
      difficulty: 'hard',
      sourceLabel: 'Head-to-head ledger',
      prompt: `Who leads the all-time head-to-head between ${manager} and ${opponent}?`,
      answer: leader,
      choices: makeChoices(leader, unique([manager, opponent, 'It is tied', ...allManagers]), `rivalry-leader-${pairKey}`),
      explanation: `${manager} is ${record} against ${opponent} in the rivalry ledger.`,
    });
  });
}

function buildWeeklyQuestions(questions, matchupRows, allManagers) {
  const byYear = groupBy(matchupRows, (row) => row.Year || row.Season);

  byYear.forEach((rows, year) => {
    const scores = [];
    rows.forEach((row) => {
      [
        ['Team1 Manager', 'Team1 Score'],
        ['Team2 Manager', 'Team2 Score'],
      ].forEach(([managerKey, scoreKey]) => {
        const manager = normalizeManager(row[managerKey]);
        const score = toNumber(row[scoreKey]);
        if (manager && score !== null) {
          scores.push({
            manager,
            score,
            week: row.Week,
          });
        }
      });
    });

    const highest = scores.slice().sort((a, b) => b.score - a.score)[0];
    if (!highest) return;

    addQuestion(questions, {
      id: `weekly-highest-score-${year}`,
      category: 'Weekly Scores',
      difficulty: 'hard',
      sourceLabel: `${year} weekly scores`,
      prompt: `Who had the highest single-week score in ${year}?`,
      answer: highest.manager,
      choices: makeChoices(highest.manager, allManagers, `weekly-highest-score-${year}`),
      explanation: `${highest.manager} had the highest single-week score in ${year}, in Week ${highest.week}.`,
    });
  });
}

export function buildKeeperGauntletQuestionBank({
  draftRows = [],
  standingsRows = [],
  championshipRows = [],
  rivalryRows = [],
  matchupRows = [],
} = {}) {
  const questions = [];
  const allRows = [
    ...draftRows,
    ...standingsRows,
    ...championshipRows,
    ...rivalryRows,
    ...matchupRows,
  ];
  const allManagers = managersFromRows(allRows);

  buildDraftQuestions(questions, draftRows, allManagers);
  buildStandingsQuestions(questions, standingsRows, allManagers);
  buildChampionshipQuestions(questions, championshipRows, allManagers);
  buildRivalryQuestions(questions, rivalryRows, allManagers);
  buildWeeklyQuestions(questions, matchupRows, allManagers);

  const seen = new Set();
  return questions.filter((question) => {
    if (seen.has(question.id)) return false;
    seen.add(question.id);
    return true;
  });
}

export function createKeeperGauntletDeck(questionBank, seed) {
  return seededShuffle(questionBank, seed);
}

export async function loadKeeperGauntletQuestionBank(fetchImpl = fetch) {
  const [
    draftSets,
    standingsRows,
    championshipRows,
    rivalryRows,
    matchupRows,
  ] = await Promise.all([
    Promise.all(GAUNTLET_DRAFT_YEARS.map((year) => loadCsv(`/draft_data/${year}_draft.csv`, fetchImpl))),
    loadCsv('/data/season_standings.csv', fetchImpl),
    loadCsv('/data/championships.csv', fetchImpl),
    loadCsv('/data/manager_rivalries.csv', fetchImpl),
    loadCsv('/data/weekly_matchups.csv', fetchImpl),
  ]);

  return buildKeeperGauntletQuestionBank({
    draftRows: draftSets.flat(),
    standingsRows,
    championshipRows,
    rivalryRows,
    matchupRows,
  });
}

async function loadCsv(path, fetchImpl) {
  const response = await fetchImpl(path);
  if (!response.ok) return [];

  const text = await response.text();
  if (!text || HTML_FALLBACK.test(text)) return [];

  return parseCSV(text);
}
